import React, { useState, useEffect } from 'react';
import { Loader2, Image as ImageIcon } from 'lucide-react';
import { getInspirations } from '../services/inspirationsService';
import { buildAssetUrl } from '../utils/urlHelpers';

const Inspirations = () => {
  const [inspirations, setInspirations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedService, setSelectedService] = useState(null);

  // Load all inspirations in batches of 200 (max allowed by API)
  const loadAllInspirations = async () => {
    setLoading(true);
    setError(null);
    setInspirations([]); // Reset inspirations when loading
    
    try {
      let allInspirations = [];
      let skip = 0;
      const limit = 200; // Max allowed by API
      let hasMore = true;
      
      // Fetch all pages in batches until no more data
      while (hasMore) {
        const response = await getInspirations({
          service_category_id: selectedCategory,
          service_id: selectedService,
          skip: skip,
          limit: limit,
          sort_by_category: true,      // Sort by category first
          sort_by_service: true,       // Then by service
          sort_by_display_order: true, // Then by display order
          category_order: 'asc',       // Ascending order for categories
          service_order: 'asc',        // Ascending order for services
          display_order: 'asc'         // Ascending order for display order
        });
        
        const data = Array.isArray(response) ? response : (response?.data || response); // Handle different response formats
        
        if (Array.isArray(data) && data.length > 0) {
          // Add new data to the collection
          allInspirations = [...allInspirations, ...data];
          
          // If we received fewer than the limit, we've reached the end
          if (data.length < limit) {
            hasMore = false;
          } else {
            skip += limit;
          }
        } else {
          hasMore = false;
        }
      }
      
      // Remove duplicates by ID to ensure unique images
      const uniqueInspirations = allInspirations.filter((item, index, self) => 
        index === self.findIndex(t => t.id === item.id)
      );
      
      setInspirations(uniqueInspirations);
    } catch (err) {
      console.error('Error loading inspirations:', err);
      setError('Falha ao carregar inspirações. Por favor, tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  // Load all inspirations when component mounts or filters change
  useEffect(() => {
    loadAllInspirations();
  }, [selectedCategory, selectedService]);

  // Organize inspirations by service for sectioned display
  const inspirationsByService = React.useMemo(() => {
    if (!inspirations || inspirations.length === 0) return [];

    // Group inspirations by service using the properly returned service_name
    const grouped = inspirations.reduce((acc, inspiration) => {
      const service = inspiration.service_name || 'Outros';
      
      if (!acc[service]) {
        acc[service] = [];
      }
      acc[service].push(inspiration);
      return acc;
    }, {});

    // Convert to array of {service, items} objects
    return Object.entries(grouped).map(([service, items]) => ({
      service,
      items
    }));
  }, [inspirations]);

  // Render an image with its category info (displaying on hover)
  const renderImage = (inspiration) => {
    // Try multiple possible image URL fields
    let imageUrl = inspiration.image_url;

    return (
      <div key={inspiration.id} className="mb-4 break-inside-avoid cursor-pointer">
        <div className="relative group overflow-hidden rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 bg-white dark:bg-brand-gray-800">
          {/* Loading skeleton */}
          {!imageUrl && (
            <div className="w-full aspect-square bg-brand-gray-200 dark:bg-brand-gray-700 animate-pulse rounded-lg" />
          )}

          {/* Image */}
          {imageUrl && (
            <img
              src={imageUrl}
              alt="Inspiração"
              className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
              onLoad={(e) => {
                // Remove skeleton when image loads
                e.target.classList.remove('opacity-0');
              }}
            />
          )}

          {/* Fallback for broken images */}
          <div
            className="w-full aspect-square flex items-center justify-center bg-brand-gray-100 dark:bg-brand-gray-800"
            style={{ display: 'none' }}
          >
            <ImageIcon size={24} className="text-brand-gray-400 dark:text-brand-gray-500" />
          </div>

          {/* Overlay - only show on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
            <div className="text-white text-xs w-full">
              <div className="font-medium truncate">
                {inspiration.category_name || inspiration.category || 'Categoria'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render a service section with images in 2 columns
  const renderServiceSection = ({ service, items }) => {
    if (!items || items.length === 0) return null;

    // Distribute items in 2 columns for Pinterest-style layout
    const column1 = [];
    const column2 = [];

    items.forEach((item, index) => {
      if (index % 2 === 0) {
        column1.push(item);
      } else {
        column2.push(item);
      }
    });

    return (
      <div key={service} className="mb-10">
        {/* Service header */}
        <div className="mb-6 flex justify-center">
          <div 
            className="px-6 py-3 rounded-lg text-white text-lg font-semibold shadow-md"
            style={{ 
              background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
              color: 'white'
            }}
          >
            {service}
          </div>
        </div>
        
        {/* Images in 2 columns */}
        <div className="flex justify-center gap-6 max-w-4xl mx-auto">
          <div className="w-1/2">
            {column1.map(renderImage)}
          </div>
          <div className="w-1/2">
            {column2.map(renderImage)}
          </div>
        </div>
      </div>
    );
  };

  // Don't render anything if no inspirations and not loading
  if (inspirationsByService.length === 0 && !loading) {
    return null;
  }

  return (
    <div className="w-full py-6">
      {/* Section header with more mobile-friendly design */}
      <div className="mb-8 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div
              className="mr-3 p-2 rounded-lg"
              style={{
                background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
              }}
            >
              <ImageIcon size={24} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-brand-dark dark:text-brand-light">Inspirações</h2>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {loading && inspirationsByService.length === 0 && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 text-pink-500 animate-spin" />
        </div>
      )}

      {/* Service sections */}
      {inspirationsByService.length > 0 && (
        <div className="space-y-10">
          {inspirationsByService.map(renderServiceSection)}
        </div>
      )}

      {/* Loading indicator for more items */}
      {loading && inspirationsByService.length > 0 && (
        <div className="flex justify-center py-6">
          <Loader2 className="h-6 w-6 text-pink-500 animate-spin" />
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="text-center py-4 text-red-500 dark:text-red-400 px-4">
          {error}
        </div>
      )}
    </div>
  );
};

export default Inspirations;